<script setup lang="ts">
interface Props {
	isOpen: boolean
	showOnStartup: boolean
}

defineProps<Props>()

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const emit = defineEmits<{
	(e: 'close'): void
	(e: 'update:showOnStartup', value: boolean): void
}>()
</script>

<template>
	<div v-if="isOpen" class="modal fade in show guide-modal" role="dialog" aria-modal="true" aria-labelledby="guideTitle">
		<div class="modal-dialog" role="document">
			<div class="modal-content">
				<div class="modal-header">
					<button type="button" class="close" :aria-label="_('Close')" @click="emit('close')"><span aria-hidden="true">&times;</span></button>
					<h4 class="modal-title" id="guideTitle">{{ _('How to use the toolbar') }}</h4>
				</div>

				<div class="modal-body">
					<p>{{ _('Use the toolbar above the canvas to edit the map. The buttons work like this:') }}</p>
					<ul class="guide-list">
						<li><strong>{{ _('Read guide') }}</strong> {{ _('opens this help again anytime.') }}</li>
						<li><strong>{{ _('Row mode') }}</strong> {{ _('allows you to edit row properties.') }}</li>
						<li><strong>{{ _('Seat mode') }}</strong> {{ _('allows you to select and delete individual seats.') }}</li>
						<li>{{ _('In seat mode, click row labels (or seats in the row) to select and drag rows.') }}</li>
						<li>{{ _('A dashed seat border means that seat has not been saved yet - if you want to remove seats, prefer removing these first over already-saved seats.') }}</li>
						<li><strong>{{ _('Grid') }}</strong> {{ _('shows or hides the editor grid lines.') }}</li>
						<li><strong>{{ _('Snap') }}</strong> {{ _('turns grid snapping on or off while you drag.') }}</li>
						<li><strong>{{ _('Add row') }}</strong> {{ _('inserts a new default row at the bottom.') }}</li>
						<li><strong>{{ _('Add screen') }}</strong> {{ _('adds a decorative screen/stage block - drag it to move, drag its bottom-right corner to resize, and use the side panel to edit its color, border and text.') }}</li>
						<li><strong>{{ _('Import') }}</strong> {{ _('loads a JSON seat map file as a layout template. Seat links are created for this resource when you save.') }}</li>
						<li><strong>{{ _('Export') }}</strong> {{ _('downloads the current seat map as JSON so you can import it on another resource.') }}</li>
						<li><strong>{{ _('Disable Selected') }} / {{ _('Enable Selected') }}</strong> {{ _('toggles selected seats or selected rows.') }}</li>
						<li><strong>{{ _('Delete selected') }}</strong> {{ _('removes anything you have selected.') }}</li>
						<li><strong>{{ _('Save') }}</strong> {{ _('saves the seat map to Johku.') }}</li>
					</ul>
				</div>

				<div class="modal-footer guide-modal-footer">
					<div class="checkbox guide-checkbox">
						<label>
							<input :checked="showOnStartup" type="checkbox" @change="emit('update:showOnStartup', ($event.target as HTMLInputElement).checked)" />
							{{ _('Show this guide when the app opens') }}
						</label>
					</div>
					<button type="button" class="btn btn-primary" @click="emit('close')">{{ _('Got it') }}</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.guide-modal {
	z-index: 2010;
}

.guide-list {
	padding-left: 18px;
	display: grid;
	gap: 8px;
}

.guide-modal-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	text-align: left;
}

.guide-checkbox {
	margin: 0;
}

@media (max-width: 480px) {
	.guide-modal-footer {
		flex-direction: column;
		align-items: stretch;
	}
}

@media print {
	.guide-modal {
		display: none;
	}
}
</style>